import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AgGridTable from '../../components/AgGridTable';
import SidebarPanel from '../../components/admin/SidebarPanel';
import EducationalTooltip from '../../components/shared/EducationalTooltip';
import RelationshipBadge from '../../components/shared/RelationshipBadge';
import ModelDocumentationModal from '../../components/shared/ModelDocumentationModal';
import AlertMessage from '../../components/shared/alertMessage';
import { adminApi } from '../../services/api';

const OrganizationsPage = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const gridRef = useRef(null);

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [alertStateSidePanel, setAlertStateSidePanel] = useState({ show: false, type: 'info', title: '', message: '' });
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ type: '', has_parent: '', is_active: 'true', search: '' });
  const [formData, setFormData] = useState({
    name: '', type: '', specialty: '', departments: '', clinics: '',
    parent: null, street_address: '', city: '', state: '', zip_code: '',
    country: 'USA', organization_url: '', is_active: true
  });

  useEffect(() => {
    fetchOrgs();
  }, [filters]);

  useEffect(() => {
    if (orgId && orgs.length > 0) {
      const org = orgs.find(o => o.id == orgId);
      if (org) handleRowClick({ data: org });
    }
  }, [orgId, orgs]);

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await adminApi.getOrganizations(params);
      setOrgs(res.data?.organizations || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load organizations', message: err.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = useCallback((event) => {
    setSelectedOrg(event.data);
    setShowPanel(true);
    setEditMode(false);
    setCreateMode(false);
    navigate(`/admin/sales/organizations/${event.data.id}`, { replace: true });
  }, [navigate]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedOrg(null);
    setEditMode(false);
    setCreateMode(false);
    navigate('/admin/sales/organizations', { replace: true });
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;
      await adminApi.updateOrganization(data.id, { [colDef.field]: newValue });
      fetchOrgs();
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to update organization', message: err.response?.data?.message || err.message });
      fetchOrgs();
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({ fileName: `organizations-${new Date().toISOString().split('T')[0]}.csv` });
  };

  const handleAddOrg = () => {
    setFormData({
      name: '', type: '', specialty: '', departments: '', clinics: '',
      parent: null, street_address: '', city: '', state: '', zip_code: '',
      country: 'USA', organization_url: '', is_active: true
    });
    setSelectedOrg(null);
    setCreateMode(true);
    setEditMode(false);
    setShowPanel(true);
    navigate('/admin/sales/organizations/create', { replace: true });
  };

  const handleEditOrg = () => {
    if (selectedOrg) {
      setFormData({
        name: selectedOrg.name || '',
        type: selectedOrg.type || '',
        specialty: selectedOrg.specialty || '',
        departments: selectedOrg.departments || '',
        clinics: selectedOrg.clinics || '',
        parent: selectedOrg.parent?.id || null,
        street_address: selectedOrg.street_address || '',
        city: selectedOrg.city || '',
        state: selectedOrg.state || '',
        zip_code: selectedOrg.zip_code || '',
        country: selectedOrg.country || 'USA',
        organization_url: selectedOrg.organization_url || '',
        is_active: selectedOrg.is_active !== false
      });
      setEditMode(true);
      setCreateMode(false);
    }
  };

  const handleSaveOrg = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editMode && selectedOrg) {
        await adminApi.updateOrganization(selectedOrg.id, formData);
        const res = await adminApi.getOrganizations({});
        const updatedOrg = res.data?.organizations?.find(o => o.id === selectedOrg.id);
        setSelectedOrg(updatedOrg || selectedOrg);
        setEditMode(false);
      } else if (createMode) {
        const res = await adminApi.createOrganization(formData);
        const newOrg = res.data?.organization;
        if (newOrg) {
          setSelectedOrg(newOrg);
          navigate(`/admin/sales/organizations/${newOrg.id}`, { replace: true });
        }
        setCreateMode(false);
      }
      fetchOrgs();
    } catch (err) {
      setAlertStateSidePanel({ show: true, type: 'error', title: 'Failed to save organization', message: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedOrg.name}?`)) return;
    try {
      await adminApi.deleteOrganization(selectedOrg.id);
      handleClosePanel();
      fetchOrgs();
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to delete organization', message: err.response?.data?.message || err.message });
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const columnDefs = [
    { headerName: 'Name', field: 'name', width: 200, editable: true },
    { headerName: 'Type', field: 'type', width: 120 },
    { headerName: 'Specialty', field: 'specialty', width: 150 },
    { headerName: 'City', field: 'city', width: 120 },
    { headerName: 'State', field: 'state', width: 80 },
    { headerName: 'Parent', field: 'parent.name', width: 150 },
    { headerName: 'Children', field: 'children_count', width: 100 },
    { headerName: 'Teams', field: 'teams_count', width: 90 },
    { headerName: 'Members', field: 'members_count', width: 100 },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: p => (
        <span className={`badge bg-${p.data?.is_active ? 'success' : 'danger'}`}>
          {p.data?.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <>
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          dismissible={true}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
          <h4 className="text-lg">
          Sales Organizations
          <EducationalTooltip title="Companies using the sales platform. Supports parent-child hierarchies for complex structures." />
        </h4>
        <p className="text-muted">Central resource for managing VCX Sales Customers</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/sales">Sales</a></li>
              <li className="breadcrumb-item active">Organizations</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="d-flex justify-content-end align-items-center mb-3">
        <button className="btn btn-primary" onClick={handleAddOrg}>
          <i className="fas fa-plus me-1" />Create New
        </button>
      </div>
      <div className='card'>
      <div className="d-flex d-flex-inline justify-content-between  p-2 mb-3">
        <div className='d-flex gap-2'> 
          <select className="form-select form-select-sm" value={filters.has_parent} onChange={e => setFilters({...filters, has_parent: e.target.value})}>
            <option value="">All Organizations</option>
            <option value="false">Top-Level Only</option>
            <option value="true">Subsidiaries Only</option>
          </select>
          <select className="form-select form-select-sm" value={filters.is_active} onChange={e => setFilters({...filters, is_active: e.target.value})}>
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <input type="text" style={{width:'300px',maxWidth:'500px'}} className="form-control form-control-sm" placeholder="Search..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
          <button className='btn btn-secondary btn-sm'><i className='fas fa-search'></i></button>
        </div>
        <div className='d-flex gap-2'>
          <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
            <i className="fas fa-book me-1" />Docs
          </button>
          <button className="btn btn-sm btn-success" onClick={handleExport}>
            <i className="fas fa-file-export me-1" />Export
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : orgs.length === 0 ? (
        <div className="alert text-secondary text-center">
          <p>No organizations found.</p>
        </div>
      ) : (
        <AgGridTable
          ref={gridRef}
          rowData={orgs}
          columnDefs={columnDefs}
          onRowClicked={handleRowClick}
          onCellValueChanged={handleCellValueChanged}
          defaultColDef={{
            flex: 1,
            minWidth: 100,
            resizable: true,
            sortable: true,
            filter: true,
          }}
          gridOptions={{
            autoSizeStrategy: {
              type: 'fitGridWidth'
            }
          }}
          getRowId={(params) => params.data.id}
        />
      )}
      </div>
      <SidebarPanel
        isOpen={showPanel}
        onClose={handleClosePanel}
        title={createMode ? "Create Organization" : (editMode ? "Edit Organization" : "Organization Details")}
        className="organization-sidebar"
      >{alertStateSidePanel.show && (
        <AlertMessage
          type={alertStateSidePanel.type}
          title={alertStateSidePanel.title}
          message={alertStateSidePanel.message}
          dismissible={true}
          onDismiss={() => setAlertStateSidePanel({ ...alertStateSidePanel, show: false })}
          className="mb-3"
        />
      )}
        {createMode ? (
          <OrganizationForm
            formData={formData}
            orgs={orgs}
            saving={saving}
            onChange={handleFormChange}
            onSave={handleSaveOrg}
            onCancel={handleClosePanel}
            isCreate={true}
          />
        ) : selectedOrg && editMode ? (
          <OrganizationForm
            formData={formData}
            orgs={orgs}
            saving={saving}
            onChange={handleFormChange}
            onSave={handleSaveOrg}
            onCancel={() => setEditMode(false)}
            isCreate={false}
          />
        ) : selectedOrg && (
          <div className="row">
            <div className="col-12">
              <div className="card overflow-hidden">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="text-primary mb-0">{selectedOrg.name}</h5>
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-primary" onClick={handleEditOrg}>
                        <i className="fa fa-pencil me-1" />Edit
                      </button>
                      <button className="btn btn-danger" onClick={handleDeleteOrg}>
                        <i className="far fa-trash-can me-1" />Delete
                      </button>
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="row">
                      <div className="col-6">
                        <h5 className="font-size-15">Type:</h5>
                        <p className="text-muted mb-0">{selectedOrg.type || 'N/A'}</p>
                      </div>
                      <div className="col-6">
                        <h5 className="font-size-15">Status:</h5>
                        <p className="mb-0">
                          <span className={`badge bg-${selectedOrg.is_active ? 'success' : 'danger'}`}>
                            {selectedOrg.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Specialty:</h5>
                        <p className="text-muted mb-0">{selectedOrg.specialty || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Departments:</h5>
                        <p className="text-muted mb-0">{selectedOrg.departments || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Clinics:</h5>
                        <p className="text-muted mb-0">{selectedOrg.clinics || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Address:</h5>
                        <p className="text-muted mb-0">
                          {selectedOrg.street_address || selectedOrg.city || selectedOrg.state ? (
                            <>
                              {selectedOrg.street_address && <>{selectedOrg.street_address}<br /></>}
                              {(selectedOrg.city || selectedOrg.state || selectedOrg.zip_code) && (
                                <>{selectedOrg.city}{selectedOrg.city && selectedOrg.state ? ', ' : ''}{selectedOrg.state} {selectedOrg.zip_code}</>
                              )}
                              {selectedOrg.country && selectedOrg.country !== 'USA' && <><br />{selectedOrg.country}</>}
                            </>
                          ) : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Website:</h5>
                        <p className="text-muted mb-0">
                          {selectedOrg.organization_url ? (
                            <a href={selectedOrg.organization_url} target="_blank" rel="noopener noreferrer">
                              {selectedOrg.organization_url}
                            </a>
                          ) : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {selectedOrg.parent && (
                      <div className="mt-4">
                        <h5 className="font-size-15">Parent Organization:</h5>
                        <RelationshipBadge label={selectedOrg.parent.name} icon="fa-building" variant="info" />
                      </div>
                    )}

                    <div className="mt-4">
                      <h5 className="font-size-15">Created At:</h5>
                      <p className="text-muted mb-0">{new Date(selectedOrg.created_at).toLocaleString()}</p>
                    </div>

                    <div className="mt-4">
                      <h5 className="font-size-15">Last Updated:</h5>
                      <p className="text-muted mb-0">{new Date(selectedOrg.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title text-primary mb-4">Statistics</h4>
                  <div className="row">
                    <div className="col-4 mb-3">
                      <div className="d-flex">
                        <div className="avatar-xs me-3">
                          <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-18">
                            <i className="fa fa-sitemap"></i>
                          </span>
                        </div>
                        <div>
                          <h5 className="font-size-14 mb-0">{selectedOrg.children_count || 0}</h5>
                          <p className="text-muted mb-0">Child Orgs</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mb-3">
                      <div className="d-flex">
                        <div className="avatar-xs me-3">
                          <span className="avatar-title rounded-circle bg-success bg-soft text-success font-size-18">
                            <i className="fa fa-users"></i>
                          </span>
                        </div>
                        <div>
                          <h5 className="font-size-14 mb-0">{selectedOrg.teams_count || 0}</h5>
                          <p className="text-muted mb-0">Teams</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 mb-3">
                      <div className="d-flex">
                        <div className="avatar-xs me-3">
                          <span className="avatar-title rounded-circle bg-info bg-soft text-info font-size-18">
                            <i className="fa fa-user"></i>
                          </span>
                        </div>
                        <div>
                          <h5 className="font-size-14 mb-0">{selectedOrg.members_count || 0}</h5>
                          <p className="text-muted mb-0">Members</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SidebarPanel>

      <ModelDocumentationModal show={showDocsModal} onHide={() => setShowDocsModal(false)} modelType="organization" />
    </>
  );
};

const OrganizationForm = ({ formData, orgs, saving, onChange, onSave, onCancel, isCreate }) => (
  <div className="row">
    <div className="col-12">
      <div className="card">
        <div className="card-body">
          <form onSubmit={onSave}>
            <div className="mb-3">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={e => onChange('name', e.target.value)}
                required
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Type</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.type}
                  onChange={e => onChange('type', e.target.value)}
                  placeholder="e.g., Hospital, Clinic, School"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Website</label>
                <input
                  type="url"
                  className="form-control"
                  value={formData.organization_url}
                  onChange={e => onChange('organization_url', e.target.value)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Specialty</label>
              <textarea
                className="form-control"
                rows="2"
                value={formData.specialty}
                onChange={e => onChange('specialty', e.target.value)}
                placeholder="e.g., Pediatric Cardiology, Gastroenterology"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Departments</label>
              <textarea
                className="form-control"
                rows="2"
                value={formData.departments}
                onChange={e => onChange('departments', e.target.value)}
                placeholder="e.g., Neurosurgery, Gastroenterology"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Clinics</label>
              <textarea
                className="form-control"
                rows="2"
                value={formData.clinics}
                onChange={e => onChange('clinics', e.target.value)}
                placeholder="e.g., Houston TMC, Houston Sugarland"
              />
            </div>

            <hr className="my-4" />
            <h5 className="font-size-14 mb-3">Address Information</h5>

            <div className="mb-3">
              <label className="form-label">Street Address</label>
              <input
                type="text"
                className="form-control"
                value={formData.street_address}
                onChange={e => onChange('street_address', e.target.value)}
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.city}
                  onChange={e => onChange('city', e.target.value)}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.state}
                  onChange={e => onChange('state', e.target.value)}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">ZIP Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.zip_code}
                  onChange={e => onChange('zip_code', e.target.value)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Country</label>
              <input
                type="text"
                className="form-control"
                value={formData.country}
                onChange={e => onChange('country', e.target.value)}
              />
            </div>

            <hr className="my-4" />
            <h5 className="font-size-14 mb-3">Hierarchy</h5>

            <div className="mb-3">
              <label className="form-label">Parent Organization</label>
              <select
                className="form-select"
                value={formData.parent || ''}
                onChange={e => onChange('parent', e.target.value || null)}
              >
                <option value="">None (Top-Level Organization)</option>
                {orgs.filter(o => !o.parent).map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <small className="text-muted">Select a parent organization to create a subsidiary relationship</small>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={e => onChange('is_active', e.target.checked)}
                  id="isActive"
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active
                </label>
              </div>
            </div>

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Saving...
                  </>
                ) : (
                  <>{isCreate ? 'Create' : 'Save'}</>
                )}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
);

export default OrganizationsPage;
